import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Package, MagnifyingGlass, Broom, Printer, Trash, PencilSimple } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function InventoryPage({ user }) {
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adjusting, setAdjusting] = useState(null);
  const [adjustForm, setAdjustForm] = useState({ pallets_to_remove: 1, reason: 'damaged', notes: '' });
  const [saving, setSaving] = useState(false);
  const [filters, setFilters] = useState({
    item_code: '',
    qc_number: '',
    batch_number: '',
    reference: '',
    location: ''
  });

  useEffect(() => {
    fetchInventory();
  }, []);

  const fetchInventory = async () => {
    try {
      const token = localStorage.getItem('warehouse_token');
      const response = await axios.get(`${API}/inventory`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInventory(response.data);
    } catch (error) {
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteInventory = async (inv) => {
    const label = `${inv.item_code || inv.item?.sku || 'item'} at ${inv.location?.code || 'unknown location'}`;
    if (!window.confirm(`Delete ${inv.quantity} pallets of ${label}? Location capacity will be freed automatically. This cannot be undone.`)) return;
    try {
      const token = localStorage.getItem('warehouse_token');
      const res = await axios.delete(`${API}/inventory/${inv.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Deleted — freed ${res.data.freed_pallets} pallet(s)`);
      fetchInventory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete inventory');
    }
  };

  const openAdjust = (inv) => {
    setAdjusting(inv);
    setAdjustForm({ pallets_to_remove: inv.quantity, reason: 'damaged', notes: '' });
  };

  const submitAdjust = async (e) => {
    e.preventDefault();
    const qty = parseInt(adjustForm.pallets_to_remove);
    if (!qty || qty <= 0) {
      toast.error('Enter a pallet count greater than 0');
      return;
    }
    if (qty > adjusting.quantity) {
      toast.error(`Only ${adjusting.quantity} pallets available`);
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('warehouse_token');
      const res = await axios.post(
        `${API}/inventory/${adjusting.id}/adjust`,
        {
          pallets_to_remove: qty,
          reason: adjustForm.reason,
          notes: adjustForm.notes || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.row_deleted) {
        toast.success(`All ${qty} pallet(s) removed (${adjustForm.reason}) — row cleared`);
      } else {
        toast.success(`Adjusted from ${res.data.pallets_before} → ${res.data.pallets_after} pallets (${adjustForm.reason})`);
      }
      setAdjusting(null);
      fetchInventory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to adjust');
    } finally {
      setSaving(false);
    }
  };

  const filtered = useMemo(() => {
    return inventory.filter(inv => {
      const itemCode = (inv.item_code || inv.item?.sku || '').toLowerCase();
      const qc = (inv.qc_number || '').toLowerCase();
      const batch = (inv.batch_number || '').toLowerCase();
      const ref = (inv.reference || '').toLowerCase();
      const loc = (inv.location?.code || '').toLowerCase();
      
      return (
        (!filters.item_code || itemCode.includes(filters.item_code.toLowerCase())) &&
        (!filters.qc_number || qc.includes(filters.qc_number.toLowerCase())) &&
        (!filters.batch_number || batch.includes(filters.batch_number.toLowerCase())) &&
        (!filters.reference || ref.includes(filters.reference.toLowerCase())) &&
        (!filters.location || loc.includes(filters.location.toLowerCase()))
      );
    });
  }, [inventory, filters]);

  const clearFilters = () => {
    setFilters({ item_code: '', qc_number: '', batch_number: '', reference: '', location: '' });
  };

  const totalPallets = filtered.reduce((sum, i) => sum + (i.quantity || 0), 0);
  const totalCartons = filtered.reduce((sum, i) => sum + ((i.quantity || 0) * (i.cartons || 0)), 0);
  const uniqueItems = new Set(filtered.map(i => i.item_code || i.item?.sku)).size;
  const uniqueLocations = new Set(filtered.map(i => i.location?.code)).size;

  const hasFilters = Object.values(filters).some(v => v.trim() !== '');

  const handlePrintStockSheet = () => {
    const now = new Date().toLocaleString();
    const filterSummary = Object.entries(filters)
      .filter(([_, v]) => v.trim() !== '')
      .map(([k, v]) => `${k.replace('_', ' ')}: ${v}`)
      .join(' • ') || 'All inventory';

    const rows = filtered.map((inv, i) => `
      <tr>
        <td class="idx">${i + 1}</td>
        <td class="mono bold">${inv.item_code || inv.item?.sku || '-'}</td>
        <td class="mono">${inv.qc_number || '-'}</td>
        <td class="mono">${inv.batch_number || '-'}</td>
        <td class="mono">${inv.reference || '-'}</td>
        <td class="mono bold">${inv.location?.code || '-'}</td>
        <td class="mono">${inv.quantity} plts${inv.cartons ? ` × ${inv.cartons} ctns` : ''}</td>
        <td class="mono">${inv.date_in || '-'}</td>
        <td class="count-box"></td>
        <td class="count-box"></td>
        <td class="notes-box"></td>
      </tr>
    `).join('');

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Physical Stock Count Sheet</title>
        <style>
          @page { size: A4 portrait; margin: 10mm; }
          * { box-sizing: border-box; }
          body { font-family: 'Helvetica', Arial, sans-serif; margin: 0; padding: 12px; color: #000; font-size: 10px; }
          .header { border-bottom: 3px solid #000; padding-bottom: 8px; margin-bottom: 12px; }
          .title { font-size: 18px; font-weight: 900; letter-spacing: 1px; margin: 0; }
          .subtitle { font-size: 10px; color: #444; margin-top: 4px; display: flex; justify-content: space-between; }
          .filter-line { font-size: 9px; margin-top: 4px; padding: 4px 6px; background: #f0f0f0; border-left: 3px solid #000; }
          .stats { display: flex; gap: 16px; margin: 8px 0; font-size: 9px; }
          .stats strong { font-size: 12px; }
          table { width: 100%; border-collapse: collapse; margin-top: 8px; font-size: 9px; }
          th, td { padding: 4px 6px; border: 1px solid #666; text-align: left; }
          th { background: #000; color: #fff; font-size: 8px; text-transform: uppercase; letter-spacing: 0.5px; }
          .mono { font-family: 'Courier New', monospace; }
          .bold { font-weight: 700; }
          .idx { text-align: center; width: 22px; }
          .count-box { width: 55px; height: 20px; background: repeating-linear-gradient(0deg, transparent, transparent 19px, #eee 19px, #eee 20px); }
          .notes-box { width: 90px; }
          .signature { margin-top: 24px; display: flex; justify-content: space-between; gap: 30px; page-break-inside: avoid; }
          .sig { flex: 1; }
          .sig-line { border-top: 1px solid #000; margin-top: 30px; padding-top: 4px; font-size: 9px; }
          .footer { margin-top: 12px; display: flex; justify-content: space-between; font-size: 8px; color: #666; border-top: 1px solid #ccc; padding-top: 6px; }
          @media print { body { padding: 0; } .no-print { display: none; } }
          tbody tr { page-break-inside: avoid; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1 class="title">PHYSICAL STOCK COUNT SHEET</h1>
          <div class="subtitle">
            <span>Warehouse Management System</span>
            <span>Printed: ${now}</span>
          </div>
          <div class="filter-line"><strong>Filters:</strong> ${filterSummary}</div>
          <div class="stats">
            <span>Records: <strong>${filtered.length}</strong></span>
            <span>Total Pallets: <strong>${totalPallets}</strong></span>
            <span>Unique Items: <strong>${uniqueItems}</strong></span>
            <span>Locations: <strong>${uniqueLocations}</strong></span>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th class="idx">#</th>
              <th>Item Code</th>
              <th>QC / Shade</th>
              <th>Batch</th>
              <th>Reference</th>
              <th>Location</th>
              <th>System Qty</th>
              <th>Date In</th>
              <th>Physical Count</th>
              <th>Variance</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>

        <div class="signature">
          <div class="sig"><div class="sig-line">Counted By (Name & Signature)</div></div>
          <div class="sig"><div class="sig-line">Verified By (Supervisor)</div></div>
          <div class="sig"><div class="sig-line">Date & Time Completed</div></div>
        </div>

        <div class="footer">
          <span>Physical Stock Count Sheet</span>
          <span>Page 1 • WMS © ${new Date().getFullYear()}</span>
        </div>

        <div class="no-print" style="margin-top: 16px; text-align: center;">
          <button onclick="window.print()" style="padding: 10px 24px; font-size: 14px; cursor: pointer;">Print Now</button>
          <button onclick="window.close()" style="padding: 10px 24px; font-size: 14px; margin-left: 10px; cursor: pointer;">Close</button>
        </div>
      </body>
      </html>
    `;

    const printWin = window.open('', '_blank', 'width=1000,height=800');
    if (!printWin) {
      toast.error('Please allow pop-ups to print the stock sheet');
      return;
    }
    printWin.document.write(html);
    printWin.document.close();
    setTimeout(() => { printWin.print(); }, 400);
  };

  if (loading) return <div className="p-8">Loading...</div>;

  return (
    <div className="p-8" data-testid="inventory-page">
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="heading text-3xl font-black text-[#09090B] mb-2" data-testid="inventory-title">
            Inventory Browser
          </h1>
          <p className="text-[#52525B]">Search stock by Item Code, QC/Shade, Batch, Reference or Location</p>
        </div>
        <button
          onClick={handlePrintStockSheet}
          disabled={filtered.length === 0}
          className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="print-stock-sheet-button"
        >
          <Printer size={18} weight="bold" />
          Print Stock Sheet
        </button>
      </div>

      <div className="card mb-6">
        <div className="flex items-center gap-2 mb-4">
          <MagnifyingGlass size={20} weight="bold" className="text-[#52525B]" />
          <h2 className="heading text-lg font-bold text-[#09090B]">Filters</h2>
          {hasFilters && (
            <button
              onClick={clearFilters}
              className="ml-auto text-sm text-[#007AFF] hover:underline flex items-center gap-1"
              data-testid="clear-filters-button"
            >
              <Broom size={16} />
              Clear all
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <div>
            <Label htmlFor="f-item-code">Item Code</Label>
            <Input
              id="f-item-code"
              value={filters.item_code}
              onChange={(e) => setFilters({ ...filters, item_code: e.target.value })}
              placeholder="e.g. ITM-001"
              className="mono"
              data-testid="filter-item-code"
            />
          </div>
          <div>
            <Label htmlFor="f-qc">QC / Shade</Label>
            <Input
              id="f-qc"
              value={filters.qc_number}
              onChange={(e) => setFilters({ ...filters, qc_number: e.target.value })}
              placeholder="e.g. QC-Blue"
              className="mono"
              data-testid="filter-qc"
            />
          </div>
          <div>
            <Label htmlFor="f-batch">Batch Number</Label>
            <Input
              id="f-batch"
              value={filters.batch_number}
              onChange={(e) => setFilters({ ...filters, batch_number: e.target.value })}
              placeholder="e.g. BATCH-2026"
              className="mono"
              data-testid="filter-batch"
            />
          </div>
          <div>
            <Label htmlFor="f-ref">Reference</Label>
            <Input
              id="f-ref"
              value={filters.reference}
              onChange={(e) => setFilters({ ...filters, reference: e.target.value })}
              placeholder="e.g. PO-123"
              className="mono"
              data-testid="filter-reference"
            />
          </div>
          <div>
            <Label htmlFor="f-loc">Location</Label>
            <Input
              id="f-loc"
              value={filters.location}
              onChange={(e) => setFilters({ ...filters, location: e.target.value })}
              placeholder="e.g. A-01-56"
              className="mono"
              data-testid="filter-location"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="card">
          <p className="text-sm text-[#52525B] mb-1">Records</p>
          <p className="heading text-2xl font-black text-[#09090B]" data-testid="stat-records">{filtered.length}</p>
        </div>
        <div className="card bg-[#007AFF]/5 border-[#007AFF]/20">
          <p className="text-sm text-[#52525B] mb-1">Total Pallets</p>
          <p className="heading text-2xl font-black text-[#09090B] mono" data-testid="stat-pallets">{totalPallets}</p>
        </div>
        <div className="card bg-[#16A34A]/5 border-[#16A34A]/20">
          <p className="text-sm text-[#52525B] mb-1">Unique Items</p>
          <p className="heading text-2xl font-black text-[#09090B]" data-testid="stat-items">{uniqueItems}</p>
        </div>
        <div className="card bg-[#F59E0B]/5 border-[#F59E0B]/20">
          <p className="text-sm text-[#52525B] mb-1">Locations Used</p>
          <p className="heading text-2xl font-black text-[#09090B]" data-testid="stat-locations">{uniqueLocations}</p>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="card text-center py-12" data-testid="no-inventory">
          <Package size={48} className="mx-auto text-[#52525B] mb-4" weight="duotone" />
          <h3 className="heading text-xl font-bold text-[#09090B] mb-2">
            {hasFilters ? 'No matches found' : 'No inventory yet'}
          </h3>
          <p className="text-[#52525B]">
            {hasFilters ? 'Try adjusting your filters or clear all' : 'Complete a Put Away to add stock'}
          </p>
        </div>
      ) : (
        <div className="card p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="inventory-table">
              <thead className="bg-[#F4F4F5] border-b border-[#E4E4E7]">
                <tr>
                  <th className="text-left p-3 text-xs font-medium text-[#52525B] uppercase tracking-wide">Item Code</th>
                  <th className="text-left p-3 text-xs font-medium text-[#52525B] uppercase tracking-wide">QC / Shade</th>
                  <th className="text-left p-3 text-xs font-medium text-[#52525B] uppercase tracking-wide">Batch</th>
                  <th className="text-left p-3 text-xs font-medium text-[#52525B] uppercase tracking-wide">Reference</th>
                  <th className="text-left p-3 text-xs font-medium text-[#52525B] uppercase tracking-wide">Location</th>
                  <th className="text-left p-3 text-xs font-medium text-[#52525B] uppercase tracking-wide">Quantity</th>
                  <th className="text-left p-3 text-xs font-medium text-[#52525B] uppercase tracking-wide">Date In</th>
                  {(user.role === 'admin' || user.role === 'manager') && (
                    <th className="text-right p-3 text-xs font-medium text-[#52525B] uppercase tracking-wide">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {filtered.map((inv) => (
                  <tr key={inv.id} className="border-b border-[#E4E4E7] even:bg-[#FAFAFA] hover:bg-[#F4F4F5]" data-testid="inventory-row">
                    <td className="p-3 mono text-sm font-bold text-[#09090B]" data-testid="inv-item-code">
                      {inv.item_code || inv.item?.sku || '-'}
                    </td>
                    <td className="p-3 mono text-sm text-[#09090B]" data-testid="inv-qc">{inv.qc_number || '-'}</td>
                    <td className="p-3 mono text-sm text-[#09090B]" data-testid="inv-batch">{inv.batch_number || '-'}</td>
                    <td className="p-3 mono text-sm text-[#52525B]" data-testid="inv-reference">{inv.reference || '-'}</td>
                    <td className="p-3 mono text-sm font-bold text-[#007AFF]" data-testid="inv-location">
                      {inv.location?.code || '-'}
                    </td>
                    <td className="p-3 mono text-sm font-bold text-[#09090B]" data-testid="inv-qty">
                      {inv.quantity} plts{inv.cartons ? ` × ${inv.cartons} ctns` : ''}
                    </td>
                    <td className="p-3 mono text-sm text-[#52525B]" data-testid="inv-date">{inv.date_in || '-'}</td>
                    {(user.role === 'admin' || user.role === 'manager') && (
                      <td className="p-3 text-right">
                        <button
                          onClick={() => openAdjust(inv)}
                          className="btn-secondary text-xs px-3 py-1 inline-flex items-center gap-1"
                          data-testid="inventory-adjust-button"
                        >
                          <PencilSimple size={14} weight="bold" />
                          Adjust
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={!!adjusting} onOpenChange={(open) => !open && setAdjusting(null)}>
        <DialogContent data-testid="adjust-inventory-dialog">
          <DialogHeader>
            <DialogTitle className="heading text-xl font-bold">Adjust Inventory</DialogTitle>
          </DialogHeader>
          {adjusting && (
            <form onSubmit={submitAdjust} className="space-y-4">
              <div className="p-3 bg-[#F4F4F5] border border-[#E4E4E7] rounded-md space-y-1">
                <p className="text-xs text-[#52525B]">Adjusting</p>
                <p className="mono font-bold text-[#09090B]">
                  {adjusting.item_code || adjusting.item?.sku || '—'}
                  {adjusting.qc_number ? ` · QC ${adjusting.qc_number}` : ''}
                  {adjusting.batch_number ? ` · Batch ${adjusting.batch_number}` : ''}
                </p>
                <p className="text-xs mono text-[#52525B]">
                  At <span className="font-bold text-[#007AFF]">{adjusting.location?.code || '—'}</span> · Currently <span className="font-bold text-[#09090B]">{adjusting.quantity} pallets</span>
                </p>
              </div>

              <div>
                <Label htmlFor="pallets_to_remove">Pallets to Remove *</Label>
                <Input
                  id="pallets_to_remove"
                  type="number"
                  min="1"
                  max={adjusting.quantity}
                  value={adjustForm.pallets_to_remove}
                  onChange={(e) => setAdjustForm({ ...adjustForm, pallets_to_remove: e.target.value })}
                  className="mono"
                  required
                  data-testid="adjust-pallets-input"
                />
                <p className="text-xs text-[#52525B] mt-1 mono">
                  {parseInt(adjustForm.pallets_to_remove) >= adjusting.quantity
                    ? `Will remove ALL ${adjusting.quantity} pallets (row cleared)`
                    : `Will leave ${adjusting.quantity - (parseInt(adjustForm.pallets_to_remove) || 0)} pallets`}
                </p>
              </div>

              <div>
                <Label htmlFor="reason">Reason *</Label>
                <Select value={adjustForm.reason} onValueChange={(v) => setAdjustForm({ ...adjustForm, reason: v })}>
                  <SelectTrigger data-testid="adjust-reason-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="damaged">Damaged pallet</SelectItem>
                    <SelectItem value="mis-scan">Mis-scan / data entry fix</SelectItem>
                    <SelectItem value="audit">Audit correction</SelectItem>
                    <SelectItem value="shipped">Shipped out (off-system)</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="notes">Notes (optional)</Label>
                <Input
                  id="notes"
                  value={adjustForm.notes}
                  onChange={(e) => setAdjustForm({ ...adjustForm, notes: e.target.value })}
                  placeholder="e.g. forklift damage during putaway"
                  data-testid="adjust-notes-input"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={saving} className="flex-1 btn-primary" data-testid="adjust-submit-button">
                  {saving ? 'Saving...' : 'Confirm Adjustment'}
                </button>
                <button type="button" onClick={() => setAdjusting(null)} className="flex-1 btn-secondary" data-testid="adjust-cancel-button">
                  Cancel
                </button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
